/** Bounded microphone-only capture for completed-turn file transcription.
 * VAD events come from the existing Realtime connection. Keep two seconds of
 * pre-roll for delayed speech-start events, and never reuse a committed turn's
 * audio. Real-device/network acceptance remains required before learner use.
 */
export const TURN_AUDIO_SAMPLE_RATE = 24000;
export const MAX_TURN_AUDIO_SECONDS = 120;
const PRE_ROLL_SECONDS = 2;

export class CompletedTurnAudioBuffer {
  private chunks: Float32Array[] = [];
  private samples = 0;
  private active = false;
  private overflow = false;

  push(chunk: Float32Array) {
    if (this.overflow) return;
    this.chunks.push(chunk.slice());
    this.samples += chunk.length;
    if (this.active && this.samples > TURN_AUDIO_SAMPLE_RATE * MAX_TURN_AUDIO_SECONDS) {
      this.clear();
      this.overflow = true;
      throw new Error('Your answer exceeded the 120-second audio limit. Please reconnect and give a shorter answer.');
    }
    if (!this.active) {
      const limit = TURN_AUDIO_SAMPLE_RATE * PRE_ROLL_SECONDS;
      while (this.samples > limit && this.chunks.length) {
        const first = this.chunks[0];
        const drop = Math.min(first.length, this.samples - limit);
        if (drop === first.length) this.chunks.shift();
        else this.chunks[0] = first.slice(drop);
        this.samples -= drop;
      }
    }
  }

  begin() {
    if (this.overflow) throw new Error('Audio capture must reconnect after exceeding the turn limit.');
    if (this.active) return;
    this.active = true;
  }

  finish(): Blob {
    if (!this.active || this.overflow) throw new Error('This audio turn could not be captured. Please speak again.');
    const samples = this.samples;
    const chunks = this.chunks;
    this.clear();
    if (samples < TURN_AUDIO_SAMPLE_RATE / 10) throw new Error('The audio turn was too short. Please speak again.');
    const wav = new ArrayBuffer(44 + samples * 2);
    const view = new DataView(wav);
    const label = (offset: number, value: string) => {
      for (let i = 0; i < value.length; i += 1) view.setUint8(offset + i, value.charCodeAt(i));
    };
    label(0, 'RIFF'); view.setUint32(4, wav.byteLength - 8, true);
    label(8, 'WAVE'); label(12, 'fmt '); view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); view.setUint16(22, 1, true);
    view.setUint32(24, TURN_AUDIO_SAMPLE_RATE, true); view.setUint32(28, TURN_AUDIO_SAMPLE_RATE * 2, true);
    view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    label(36, 'data'); view.setUint32(40, samples * 2, true);
    let offset = 44;
    chunks.forEach((chunk) => {
      chunk.forEach((sample) => {
        const clipped = Math.max(-1, Math.min(1, Number.isFinite(sample) ? sample : 0));
        view.setInt16(offset, clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff, true);
        offset += 2;
      });
    });
    return new Blob([wav], { type: 'audio/wav' });
  }

  clear() {
    this.chunks = [];
    this.samples = 0;
    this.active = false;
  }
}

export type CompletedTurnAudioCapture = {
  begin: () => void;
  finish: () => Blob;
  close: () => void;
};

export const createCompletedTurnAudioCapture = async (
  stream: MediaStream,
  onError: (error: Error) => void,
  signal?: AbortSignal,
): Promise<CompletedTurnAudioCapture> => {
  if (typeof AudioContext === 'undefined' || typeof AudioWorkletNode === 'undefined') {
    throw new Error('Completed-turn transcription requires a browser with AudioWorklet support. Select the existing transcriber to continue.');
  }
  const context = new AudioContext({ sampleRate: TURN_AUDIO_SAMPLE_RATE });
  let source: MediaStreamAudioSourceNode | null = null;
  let node: AudioWorkletNode | null = null;
  let closed = false;
  const buffer = new CompletedTurnAudioBuffer();
  const close = () => {
    if (closed) return;
    closed = true;
    signal?.removeEventListener('abort', close);
    if (node) { node.port.onmessage = null; node.port.close(); node.disconnect(); }
    source?.disconnect();
    buffer.clear();
    void context.close().catch(() => undefined);
  };
  signal?.addEventListener('abort', close, { once: true });
  const ready = async (operation: Promise<void>) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([operation, new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Audio capture did not start. Please reconnect or select the existing transcriber.')), 5000);
      })]);
      if (closed || signal?.aborted) throw new Error('Audio capture was cancelled.');
    } finally { if (timer !== undefined) clearTimeout(timer); }
  };
  try {
    if (signal?.aborted) throw new Error('Audio capture was cancelled.');
    if (context.sampleRate !== TURN_AUDIO_SAMPLE_RATE) {
      throw new Error('This browser cannot capture the required 24 kHz audio. Select the existing transcriber.');
    }
    await ready(context.audioWorklet.addModule(`${process.env.PUBLIC_URL || ''}/voice-turn-capture-worklet.js`));
    source = context.createMediaStreamSource(stream);
    node = new AudioWorkletNode(context, 'voice-turn-capture', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1] });
    node.port.onmessage = ({ data }: MessageEvent<Float32Array>) => {
      if (closed) return;
      try { buffer.push(data); } catch (error) { close(); onError(error as Error); }
    };
    node.onprocessorerror = () => {
      if (closed) return;
      close();
      onError(new Error('Audio capture stopped unexpectedly. No answer was saved. Please reconnect.'));
    };
    source.connect(node);
    node.connect(context.destination);
    await ready(context.resume());
    return { begin: () => buffer.begin(), finish: () => buffer.finish(), close };
  } catch (error) {
    close();
    throw error;
  }
};
